import { useTranslation } from 'react-i18next'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { formatUnit, unitLabel, type FuelQuantity, type WorkedWeightBalance } from '@jupi/shared'
import { useUnitSystem } from '@jupi/ui'

// The masses from the empty aircraft to landing, with the fuel burned at
// each step in litres, US gallons and kg.
const f = (n: number) => n.toFixed(1)

export default function WeightLadderCard({ worked }: { worked: WorkedWeightBalance }) {
  const { t } = useTranslation()
  const system = useUnitSystem()
  const { ladder: l } = worked
  const mu = unitLabel('mass', system)
  const m = (kg: number) => formatUnit('mass', kg, system, 1)
  const fuelCells = (q: FuelQuantity | null, sign = '') =>
    q ? (
      <>
        <TableCell align="right">{sign}{f(q.litres)}</TableCell>
        <TableCell align="right">{sign}{f(q.usg)}</TableCell>
      </>
    ) : (
      <>
        <TableCell />
        <TableCell />
      </>
    )
  const row = (key: string, label: string, kg: number, fuel: FuelQuantity | null, opts: { bold?: boolean; sign?: string } = {}) => (
    <TableRow key={key} sx={opts.bold ? { '& td, & th': { fontWeight: 700 } } : undefined}>
      <TableCell sx={{ pl: 0 }}>{label}</TableCell>
      <TableCell align="right">{opts.sign}{m(kg)}</TableCell>
      {fuelCells(fuel, opts.sign)}
    </TableRow>
  )
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('plan.worked.weights')}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ pl: 0 }} />
              <TableCell align="right">{mu}</TableCell>
              <TableCell align="right">l</TableCell>
              <TableCell align="right">USG</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row('bew', t('plan.worked.basicEmpty'), l.basicEmptyKg, null)}
            {row('pay', t('plan.worked.payload'), l.payloadKg, null, { sign: '+ ' })}
            {row('zfw', t('plan.worked.zeroFuel'), l.zeroFuelKg, null, { bold: true })}
            {row('fuel', t('plan.worked.fuelLoaded'), l.fuelLoaded.kg, l.fuelLoaded, { sign: '+ ' })}
            {row('ramp', t('plan.worked.ramp'), l.rampKg, null, { bold: true })}
            {row('taxi', t('plan.worked.taxiFuel', { minutes: l.taxi.minutes }), l.taxi.kg, l.taxi, { sign: '− ' })}
            {row('tow', t('plan.worked.takeoffWeight'), l.takeoffKg, null, { bold: true })}
            {row('trip', t('plan.worked.tripFuelMin', { minutes: l.trip.minutes }), l.trip.kg, l.trip, { sign: '− ' })}
            {row('ldg', t('plan.worked.landingWeight'), l.landingKg, null, { bold: true })}
            {row('left', t('plan.worked.landingFuel'), l.landingFuel.kg, l.landingFuel)}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
